import { useState } from 'react';
import { useQuery } from '@apollo/client';
import { useSearchParams } from 'react-router-dom';
import FreshnessBadge from '../../components/FreshnessBadge';
import ModuleShell from '../../components/ModuleShell';
import RefreshRollupButton, {
  useRollupRefreshMessage,
} from '../../components/RefreshRollupButton';
import { ADMIN_AUDIT_LOG, ADMIN_JOB_STATUS } from '../../graphql/adminQueries';
import { formatDateTime } from '../../utils/format';

const PAGE_SIZE = 25;

const AUDIT_ACTIONS = [
  { value: '', label: 'All actions' },
  { value: 'admin.tier_override.set', label: 'Tier override set' },
  { value: 'admin.tier_override.remove', label: 'Tier override remove' },
  { value: 'admin.user.viewed', label: 'User viewed' },
  { value: 'admin.experiment.status_changed', label: 'Experiment status' },
];

export default function OperationsModule() {
  const [searchParams, setSearchParams] = useSearchParams();
  const targetId = searchParams.get('targetId') || '';
  const [offset, setOffset] = useState(0);
  const [actionFilter, setActionFilter] = useState('');

  const [refreshMessage, setRefreshMessage] = useRollupRefreshMessage();
  const {
    data: jobData,
    loading: jobLoading,
    error: jobError,
    refetch: refetchJobs,
  } = useQuery(ADMIN_JOB_STATUS);

  const { data, loading, error, refetch } = useQuery(ADMIN_AUDIT_LOG, {
    variables: {
      limit: PAGE_SIZE,
      offset,
      action: actionFilter || null,
      targetId: targetId || null,
    },
    fetchPolicy: 'network-only',
  });

  const jobStatus = jobData?.adminJobStatus;
  const result = data?.adminAuditLog;
  const entries = result?.entries || [];
  const total = result?.total ?? 0;
  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = Math.min(offset + PAGE_SIZE, total);
  const hasPrev = offset > 0;
  const hasNext = offset + PAGE_SIZE < total;

  const handleActionChange = (event) => {
    setActionFilter(event.target.value);
    setOffset(0);
  };

  const clearTargetFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('targetId');
    setSearchParams(next);
    setOffset(0);
  };

  return (
    <ModuleShell
      title="Operations"
      description="Admin audit trail and background job health."
      loading={jobLoading}
      loadingLabel="Loading job status…"
      error={jobError}
      errorLabel="Could not load job status. Confirm API adminJobStatus query is deployed."
    >
      {!jobLoading && !jobError && jobStatus && (
        <div className="product-grid">
          <section className="chart-card health-card">
            <h2>{jobStatus.rollupJobName || 'Admin rollup job'}</h2>
            <p className="chart-hint">Populates admin_daily_metrics via adminRollupJob.js</p>
            <FreshnessBadge
              label="Rollup"
              asOf={jobStatus.rollupAsOf}
              status={jobStatus.rollupStatus}
            />
            <p className="link-hint">
              Latest metric date:{' '}
              {jobStatus.latestRollupDate
                ? String(jobStatus.latestRollupDate).slice(0, 10)
                : '—'}
            </p>
            <RefreshRollupButton
              onMessage={setRefreshMessage}
              onRefreshed={() => refetchJobs()}
            />
            {refreshMessage && <p className="module-status">{refreshMessage}</p>}
            <p>
              <a
                href="https://gitlab.com/null-space-productions/null-world-productions/fiscal-flowcast/-/blob/main/APIs/website-api-group/website-api/scripts/adminRollupJob.js"
                target="_blank"
                rel="noreferrer"
              >
                Rollup job script
              </a>
            </p>
          </section>

          <section className="chart-card health-card">
            <h2>{jobStatus.stripeCacheJobName || 'Stripe MRR cache'}</h2>
            <p className="chart-hint">Hourly admin_stripe_snapshots refresh</p>
            <FreshnessBadge
              label="Stripe cache"
              asOf={jobStatus.stripeCacheAsOf}
              status={jobStatus.stripeCacheStatus}
            />
            <p>
              <a href="https://console.cloud.google.com/cloudscheduler" target="_blank" rel="noreferrer">
                Cloud Scheduler
              </a>
            </p>
          </section>
        </div>
      )}

      <section className="chart-card">
        <div className="module-header-row">
          <div>
            <h2>Audit log</h2>
            <p className="chart-hint">Append-only trail for admin mutations and support views</p>
            {targetId && (
              <p className="chart-hint">
                Filtered to target ID <code>{targetId}</code>{' '}
                <button type="button" className="text-button" onClick={clearTargetFilter}>
                  Clear
                </button>
              </p>
            )}
          </div>
          <div className="module-actions">
            <label htmlFor="audit-action-filter" className="range-label">
              Action
            </label>
            <select
              id="audit-action-filter"
              value={actionFilter}
              onChange={handleActionChange}
            >
              {AUDIT_ACTIONS.map((option) => (
                <option key={option.value || 'all'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="secondary-button"
              onClick={() => refetch()}
              disabled={loading}
            >
              Refresh
            </button>
          </div>
        </div>

        {loading && <p className="module-status">Loading audit entries…</p>}
        {error && (
          <p className="module-status module-status-error">
            Could not load audit log. Confirm the API is deployed with adminAuditLog query.
          </p>
        )}
        {!loading && !error && entries.length === 0 && (
          <p className="chart-empty">No audit entries match this filter.</p>
        )}
        {!loading && !error && entries.length > 0 && (
          <>
            <div className="table-scroll">
              <table className="data-table operations-audit-table">
                <thead>
                  <tr>
                    <th scope="col">Time</th>
                    <th scope="col">Action</th>
                    <th scope="col">Target type</th>
                    <th scope="col">Target ID</th>
                    <th scope="col">Metadata</th>
                    <th scope="col">Actor ID</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((entry) => (
                    <tr key={entry.id}>
                      <td>{formatDateTime(entry.createdAt)}</td>
                      <td>{entry.action}</td>
                      <td>{entry.targetType ?? '—'}</td>
                      <td className="operations-target-id">{entry.targetId ?? '—'}</td>
                      <td className="operations-metadata">
                        {entry.metadata && typeof entry.metadata === 'object'
                          ? JSON.stringify(entry.metadata)
                          : '—'}
                      </td>
                      <td>{entry.actorId}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="pagination-row">
              <p className="pagination-summary">
                Showing {pageStart}–{pageEnd} of {total}
              </p>
              <div className="pagination-actions">
                <button
                  type="button"
                  className="secondary-button"
                  disabled={!hasPrev || loading}
                  onClick={() => setOffset((prev) => Math.max(prev - PAGE_SIZE, 0))}
                >
                  Previous
                </button>
                <button
                  type="button"
                  className="secondary-button"
                  disabled={!hasNext || loading}
                  onClick={() => setOffset((prev) => prev + PAGE_SIZE)}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </section>

      <p className="module-footnote">
        Tier override mutations and user profile views write to this log automatically. Live
        verification requires a seeded ADMIN account with MFA.
      </p>
    </ModuleShell>
  );
}
